import React from 'react';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import DataGrid from '../../Component/DataGrid/DataGrid';
import './TransactionLogs.css';
import { TableColumn } from 'react-data-table-component';
import TransactionService from '../../Services/TransactionService';
import { amountFormat } from '../../Component/helper';
import moment from 'moment';
import { ITransactionLogs, TransactionLogsProps, TransactionLogsState } from './Interface';
import TransactionDetails from './TransactionDetails/TransactionDetails';
import CustomMaterialMenu from '../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import { ShowAlert } from '../../Component/ShowAlert';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import hacker from '../../Images/hacker.png';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import { ISelectedDateRangeString } from '../../Component/DataGrid/Interface';
import ReusableDrawer from '../../Component/RightDrawer/ReusableDrawer';
import { fetchRecipientCountryList, handleDrawerApplyCommon } from '../../Component/Reusables/Recipient';
import { getLast30DaysRange, getLast7DaysRange } from '../../Component/Reusables/Utility';

class TransactionLogs extends React.Component<TransactionLogsProps, TransactionLogsState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    private tableColumns: TableColumn<ITransactionLogs>[] = [
        {
            name: 'Transaction Id',
            selector: row => row.payment_transaction_id,
            sortable: false,
            id: "TransactionId",
            minWidth: "120px",
            wrap: true,
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            id: "Name",
            minWidth: "250px",
            wrap: true,
        },
        {
            name: 'Sender Email',
            selector: row => row.sender_email,
            sortable: true,
            id: "email",
            minWidth: "200px",
            wrap: true
        },
        {
            name: 'Sender Mobile',
            selector: row => row.sender_mobile,
            sortable: true,
            id: "mobile",
            width: "150px",
        },
        {
            name: 'Recipient',
            selector: row => row.recipient_name,
            sortable: true,
            id: "Recipient",
            minWidth: "250px",
            wrap: true,
        },
        {
            name: 'Recipient Country',
            selector: row => row.recipient_country_id,
            sortable: true,
            id: "RecipientCountry",
            // minWidth: "250px",
            wrap: true,
        },
        {
            name: 'Paid Amount',
            selector: row => amountFormat(row.paid_amount, 'en-US', 'USD'),
            sortable: true,
            id: "PaidAmount",
            right: true
        },
        {
            name: 'Payment Type',
            selector: row => row.payment_type,
            sortable: true,
            id: "payment_type",
            center: true,
            minWidth: '150px'
        },
        {
            name: 'Status',
            selector: row => row.payment_status,
            sortable: true,
            id: "status",
            center: true
        },
        {
            name: 'Compliance',
            selector: row => row.compliance,
            sortable: true,
            id: "compliance",
            cell: row => <div className='center-cell'>{row.compliance ? <SpeakerNotesOffIcon /> : '-'}</div>,
            minWidth: '120px'
        },
        {
            name: 'Fraud Detection',
            selector: row => row.fraud_detection,
            sortable: true,
            id: "fraud_detection",
            cell: row => <div className='center-cell'>{row.fraud_detection ? <img src={hacker} alt="hacker" /> : '-'}</div>
        },
        {
            name: 'Date',
            selector: row => moment(row.payment_time).format('MM-DD-YYYY hh:mm A'),//hh:mm A on Do MMMM YYYY
            sortable: true,
            id: "date",
            width: "170px",
            center: true
        },
        {
            name: 'Actions',
            cell: (row) => {
                const { transactionLogs } = this.state

                if (!transactionLogs.isFraudMarkVisible) return null;
                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={[
                            ...(row.fraud_detection
                                ? [{ key: "unMarkFraud", label: "Unmark Fraud" }]
                                : [{ key: "markFraud", label: "Mark Fraud" }]),
                            ...(row.payment_status?.trim().toLowerCase() !== "refunded" &&
                                row.transaction_mode?.trim().toLowerCase() === "card" &&
                                row.stripe_status?.trim().toLowerCase() === "captured"
                                ? [{ key: "voidPayment", label: "Void Payment" }]
                                : [])
                        ]}
                        onPopoverClick={(key_, label_) => {
                            if (key_ === "voidPayment") {
                                this.voidPayment(row);
                            } else {
                                this.updateFraud(row, key_, label_);
                            }
                        }}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: "120px",
        }
    ]

    constructor(props_: TransactionLogsProps) {
        super(props_);
        let URLParams = new URLSearchParams(window.location.search);
        let transactionId = URLParams.get("id");
        let monthRange = getLast7DaysRange();
        this.state = {
            accessToken: "",
            transactionData: [],
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 100,
            searchText: transactionId ? transactionId : "",
            selectedDateRange:
            {
                startDate: monthRange.startDate,
                endDate: monthRange.endDate,
            },
            contentRef: React.createRef<HTMLDivElement>(),
            csvData: [],
            transactionLogs: {
                isVisible: true,
                isDownloadVisible: true,
                isFraudMarkVisible: true,
                isRefundVisible: true,
            },
            isDrawerOpen: false,
            selectedCountry: "",
            selectedTxnStatus: "",
            appliedFiltersCount: 0
        }
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken, appliedFiltersCount: this.state.searchText ? 0 : 1 }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.transactionLogs?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const transactionLogs = permissionResponse.permissions.transactionLogs

                        this.setState({ transactionLogs }, () => {
                            this.getTransactionHistory();
                        });

                    } else {
                        this.getTransactionHistory();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }


    fetchDropdownOptions = () => {
        return fetchRecipientCountryList(this.state.accessToken);
    };

    fetchTxnStatusDropdownOptions = async () => {
        const response = await TransactionService.fetchTransactionStatusList(this.state.accessToken);

        if (response?.ok) {
            const result = await response.json();
            console.log(result);
            return result;
        }

        return [];
    };

    handleDrawerApply = (filters: { selectedId: string; startDate: string | null; endDate: string | null; selectedTxnStatus: string }) => {
        const { selectedId, startDate, endDate, selectedTxnStatus } = filters;

        this.setState(
            {
                selectedCountry: selectedId || "",
                selectedDateRange: {
                    startDate: startDate || null,
                    endDate: endDate || null,
                },
                selectedTxnStatus: selectedTxnStatus,
                currentPage: 1,
            },
            () => {
                this.getTransactionHistory();
            }
        );
    };


    public render() {
        const { transactionLogs } = this.state;

        return (
            <div className='body-sub-contianer table-container'>
                {/* <h1>Transaction Logs</h1> */}
                <DataGrid
                    title={this.props.userId ? "" : 'Transaction Logs'}
                    gridId={this.props.gridId}
                    search='Transaction Logs'
                    //isDatePickerNeeded
                    isClearDateOnSearch
                    defaultSearch={this.state.searchText}
                    onSearch={(searchText_: string, selectedDateRange: ISelectedDateRangeString | null) => {
                        const newDate = selectedDateRange === null ? { startDate: null, endDate: null } : selectedDateRange;
                        this.setState({
                            searchText: searchText_,
                            currentPage: 1,
                            selectedDateRange: newDate,
                            appliedFiltersCount: searchText_ ? 0 : selectedDateRange !== null ? 1 : this.state.appliedFiltersCount,
                            selectedCountry: searchText_ ? '' : this.state.selectedCountry
                        }, () => {
                            this.getTransactionHistory();
                        })
                    }}
                    onApply={(filteredDate: { startDate: string, endDate: string }) => {
                        this.setState({
                            selectedDateRange: filteredDate,
                            currentPage: 1
                        }, () => {
                            this.getTransactionHistory();
                        })
                    }}
                    columns={this.tableColumns}
                    data={this.state.transactionData}
                    csvData={this.state.csvData}
                    expandableRows
                    onChangePage={(page: number, totalRows: number) => {
                        //console.log(page, totalRows);
                        this.setState({
                            currentPage: page
                        }, () => {
                            this.getTransactionHistory();
                        })
                    }}
                    onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                        //console.log(currentRowsPerPage_, currentPage_);
                        this.setState({
                            currentRowsPerPage: currentRowsPerPage_,
                            currentPage: currentPage_
                        }, () => {
                            this.getTransactionHistory();
                        })
                    }}
                    pagination
                    paginationTotalRows={this.state.totalRows}
                    expandedComponent={(data_: any) => <TransactionDetails
                        transactionLogs={transactionLogs}
                        details={data_.data}
                        accessToken={this.state.accessToken}
                        onRefundDone={(pageRefresh: boolean) => {
                            if (pageRefresh) {
                                this.getTransactionHistory();
                            }
                        }} />}
                    exportCsv={transactionLogs.isDownloadVisible ? true : false}
                    isDrawerRequired={true}
                    isDrawerOpen={() => this.setState({ isDrawerOpen: true })}
                    appliedFiltersCount={this.state.appliedFiltersCount}
                    selectedCountry={this.state.selectedCountry}
                    isExportCSVForTransactionLog
                />
                <ReusableDrawer
                    open={this.state.isDrawerOpen}
                    onClose={() => this.setState({ isDrawerOpen: false })}
                    onApply={this.handleDrawerApply}
                    fetchDropdownOptions={this.fetchDropdownOptions}
                    defaultValue={this.state.selectedCountry}
                    onFiltersCountChange={(count) => this.setState({ appliedFiltersCount: count })}
                    defaultStartDate={this.state.searchText ? '' : this.state.selectedDateRange.startDate}
                    defaultEndDate={this.state.searchText ? '' : this.state.selectedDateRange.endDate}
                    fetchTxnStatusDropdownOptions={this.fetchTxnStatusDropdownOptions}
                    defaultTxnStatus={this.state.selectedTxnStatus}
                    showDateRange={true}
                    showTxnStatusFilter={true}
                />
            </div>
        )
    }

    public getCSVData(startDate: string | null, endDate: string | null) {
        let postData: any = {
            "startDate": startDate,
            "endDate": endDate
        }
        if (this.state.searchText) {
            postData.searchText = this.state.searchText
        }
        TransactionService.getTransactionHistoryForCSV(this.state.accessToken, postData, this.props.userId).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    this.setState({
                        csvData: resp.transactionList
                    }, () => {
                        //console.log(this.state.csvData);
                    })
                });
            }
        });
    }

    private getTransactionHistory() {
        let pageIndex: number = this.state.currentPage;
        let monthRange = getLast30DaysRange();
        let startDate, endDate;
        console.log(this.state.selectedDateRange.startDate, this.state.searchText);
        
        if(this.state.selectedDateRange.startDate == '' || this.state.selectedDateRange.startDate == "Invalid date") {
            this.setState({
                selectedDateRange: {
                    startDate: monthRange.startDate,
                    endDate: monthRange.endDate,
                },
                appliedFiltersCount : 1
            });
            startDate = monthRange.startDate;
            endDate = monthRange.endDate;
        } else {
            startDate = this.state.selectedDateRange.startDate;
            endDate = this.state.selectedDateRange.endDate;
        }
        // const startDate = this.state.selectedDateRange.startDate;
        // const endDate = this.state.selectedDateRange.endDate;
        // const startDate = this.state.selectedDateRange.startDate ? monthRange.startDate : this.state.selectedDateRange.startDate;
        // const endDate = this.state.selectedDateRange.endDate ? monthRange.endDate : this.state.selectedDateRange.endDate;
        let postData: any
        postData = {
            "pageNumber": pageIndex,
            "recordPerPage": this.state.currentRowsPerPage,
            "startDate": startDate,
            "endDate": endDate
        }
        if (this.state.searchText) {
            postData["searchText"] = this.state.searchText;
            postData["startDate"] = null;
            postData["endDate"] = null;
        }
        if (this.state.selectedCountry) {
            postData["countryId"] = this.state.selectedCountry
        }
        if (typeof this.state.selectedTxnStatus === "number") {
            postData["txnStatusId"] = this.state.selectedTxnStatus
        }

        //console.log(postData, '=========> postDate');
        TransactionService.getTransactionHistory(this.state.accessToken, postData, this.props.userId).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    this.setState({
                        transactionData: resp.transactionList,
                        totalRows: resp.totalRecords
                    })
                });
            }
        });
        this.getCSVData(startDate, endDate);
    }

    private async updateFraud(row_: ITransactionLogs, key_: string, label_: string) {
        let postdata: any = {
            "transactionId": row_.payment_transaction_id,
            "fraudDetection": key_ == "markFraud"
        }

        let tr = await TransactionService.updateFraud(this.state.accessToken, postdata);
        let message = key_ == "markFraud" ? "fraudulent" : "not fraudulent";

        if (tr.ok) {
            ShowAlert("PassportPay", `The transaction has been marked as ${message}.`, 'success').then(() => {
                this.getTransactionHistory();
            });
        }
    }

    private async voidPayment(row_: ITransactionLogs) {
        const postdata = {
            externalTransactionId: row_.payment_transaction_id,
            amount: row_.paid_amount,
        };

        try {
            const tr = await TransactionService.voidPayment(this.state.accessToken, postdata);

            if (tr.ok) {
                const message = "Void payment successfully initiated.";
                await ShowAlert("Sucsess", message, "success");

                this.getTransactionHistory();
            }
        } catch (error) {
            console.error(error);
            ShowAlert("PassportPay", "An error occurred while voiding the payment.", "error");
        }
    }
}

export default TransactionLogs;